/*
 * © Copyright 2014 – 2023 Open Text or one of its affiliates.
 *
 * The only warranties for products and services of Open Text and its affiliates and licensors ("Open Text") are as
 * may be set forth in the express warranty statements accompanying such products and services. Nothing herein should be
 * constituting an additional warranty. Open Text shall not be liable for technical or editorial errors or omissions
 * contained herein.
 *
 * The information contained herein is subject to change without notice.
 */

import './EnrolledMethods.scss';
import Tile, {TYPE_ADD} from '../tiles/Tile';
import React from 'react';
import {connect} from 'react-redux';
import {
    viewAvailableChains, viewAvailableMethods, viewChainAuthenticator, viewAuthenticator
} from '../../actions/navigation.actions';
import PropTypes from 'prop-types';
import {
    categoriesType, indexedChainsType, availableIndexedTemplatesType, enrolledTemplatesType
} from '../../types/types';
import {getTemplateKey} from '../../utils/key-generator';
import ChainTileList from '../../components/tiles/ChainTileList';
import MethodTile from '../../components/tiles/MethodTile';
import {fetchIndexedData} from '../../actions/methods-display.actions';
import getCategoryName from '../../utils/category-name';
import t from '../../i18n/locale-keys';
import {autocreatedMethods, unenrollableMethods} from '../../data/MethodData';
import withParams from '../WithParams';

class EnrolledMethods extends React.PureComponent {
    constructor(props) {
        super(props);

        this.props.fetchIndexedData();
    }

    render() {
        const {alwaysHideCategories, categories, enrolledTemplates, indexedChains, nonDefaultCategoriesEnrolled,
            viewChainAuthenticator} = this.props;
        let methodElements = null;
        if (enrolledTemplates) {
            methodElements = enrolledTemplates.map((template) => {
                if (unenrollableMethods.indexOf(template.methodId) !== -1) {
                    //Don't display unenrollable methods
                    return null;
                }
                const categoryName = nonDefaultCategoriesEnrolled
                    ? getCategoryName(categories, template.categoryId) : null;
                const key = getTemplateKey(template);
                const handleClick = () => this.props.viewAuthenticator(template);
                return (
                    <MethodTile
                        categoryName={categoryName}
                        id={key}
                        key={key}
                        onClick={handleClick}
                        template={template}
                    />
                );
            });
        }
        let chainTileList = null;
        if (indexedChains) {
            chainTileList = (
                <ChainTileList
                    alwaysHideCategories={alwaysHideCategories}
                    categories={categories}
                    enrolled
                    indexedChains={indexedChains}
                    nonDefaultCategoriesEnrolled={nonDefaultCategoriesEnrolled}
                    viewChainAuthenticator={viewChainAuthenticator}
                />
            );
        }
        let consentBlock = null;
        let isAutoCreatedMethods = true;
        for (let i = 0; i < enrolledTemplates.length; i++) {
            isAutoCreatedMethods = autocreatedMethods.includes(enrolledTemplates[i].methodId);
            if (!isAutoCreatedMethods) {
                break;
            }
        }
        if (this.props.policies && this.props.policies.deleteMeOptions.data.deleteMeEnabled && !isAutoCreatedMethods) {
            consentBlock = (
                <div className="consent-block">
                    <div className="ias-status-message">
                        <div className="ias-status-message-title">
                            <i className="ias-icon ias-icon-status_info_thick ias-info" />
                            <span>{t.enrollmentConsentTitle()}</span>
                        </div>
                        <p>
                            {t.enrollmentConsentDescription()}
                        </p>
                    </div>
                </div>
            );
        }
        let methodsEnrollment = null;
        if (this.props.policies && !this.props.policies.enrollmentOptions.data.hideMethods) {
            methodsEnrollment = (
                <div>
                    <div className="methods-grid-label">{t.enrolledMethodsSubtitle()}</div>
                    <p className="description">
                        {t.enrolledSingleMethodsDescription()}
                    </p>
                    <div className="ias-grid">
                        {methodElements}
                        <Tile
                            icon="new_thin"
                            id="add-methods-tile"
                            key="Add"
                            onClick={this.props.viewAvailableMethods}
                            title={t.addLabel()}
                            type={TYPE_ADD}
                        />
                    </div>
                </div>
            );
        }
        let chainsEnrollment = null;
        if (this.props.policies && !this.props.policies.enrollmentOptions.data.hideChains) {
            chainsEnrollment = (
                <div>
                    <div className="chains-grid-label">{t.enrolledChainsSubtitle()}</div>
                    <p className="description">
                        {t.enrolledSequencesDescription()}
                    </p>
                    <div className="ias-grid">
                        {chainTileList}
                        <div className="add-chain-container">
                            <Tile
                                icon="new_thin"
                                id="add-chains-tile"
                                key="Add"
                                onClick={this.props.viewAvailableChains}
                                title={t.addLabel()}
                                type={TYPE_ADD}
                            />
                        </div>
                    </div>
                </div>
            );
        }
        return (
            <div className="ias-content-padding">
                <h2>{t.enrolledMethodsTitle()}</h2>
                <p className="description">
                    {t.enrolledMethodsDescription()}
                </p>
                <p className="description">
                    {t.enrolledMethodsDescriptionPartTwo()}
                </p>
                {methodsEnrollment}
                {chainsEnrollment}

                {consentBlock}
            </div>
        );
    }
}

EnrolledMethods.propTypes = {
    availableIndexedTemplates: availableIndexedTemplatesType,
    categories: categoriesType,
    enrolledTemplates: enrolledTemplatesType,
    indexedChains: indexedChainsType,
    nonDefaultCategoriesEnrolled: PropTypes.bool
};

const mapStateToProps = ({methodsDisplay: {categories, enrolledTemplates, indexedData, policies}}) => {
    return {...indexedData, categories, enrolledTemplates, policies};
};

const mapDispatchToProps = {
    fetchIndexedData,
    viewAvailableChains,
    viewAvailableMethods,
    viewChainAuthenticator,
    viewAuthenticator
};

export default withParams(connect(mapStateToProps, mapDispatchToProps)(EnrolledMethods));
